import axios from 'axios'
import { getUrlParams } from '@/utils/url'
import { supportFormatWebp } from '@/utils/general'
import App from '../main.js'

const { lang, campground } = getUrlParams()

let baseURL = process.env.VUE_APP_API_ENDPOINT
let baseDomain = 'api.campnerd-changer.com'
const baseDomainParts = baseURL.split('.')
const len = baseDomainParts.length
if (len > 2) {
  baseDomain = `${baseDomainParts[len - 2]}.${baseDomainParts[len - 1]}`
}
const backend = new URLSearchParams(window.location.search).get('backend')
if (backend && backend.match(/^[a-z0-9_-]+$/i)) {
  baseURL = `https://${backend}.${baseDomain}`
}

baseURL = `${baseURL}/{lang}/api`

let TOKEN = null

// exposing HTTP is needed for mocking in tests
export const HTTP = axios.create({
  baseURL: baseURL.replace('{lang}', lang),
})

/* istanbul ignore next */
HTTP.interceptors.request.use(config => {
  App.$Progress.start() // for every request start the progress
  if (App.$store.getters['booking/aid']) {
    config.headers['CAMPNERD-CHANGER-AID'] = App.$store.getters['booking/aid']
  }
  return config
})

/* istanbul ignore next */
HTTP.interceptors.response.use(response => {
  App.$Progress.finish() // finish when a response is received
  return response
})

// exposed api
export const Api = {
  getCampgroundData: language => getCampgroundData(language),
  getData: (url, params) => getData(url, params),
  getAccommodationAttributes,
  getCampsiteAttributes,
  createStripePaymentIntent,
  getStripePaymentStatus,
  postData: (url, params) => postData(url, params),
  setToken: token => {
    TOKEN = token
  },
}

async function getAccommodationAttributes(campground) {
  // eslint-disable-next-line prettier/prettier
    return (await HTTP.get(
    `accommodation-typ-attributes-entries/${campground}/`,
    getConfigWithToken()
  )).data
}

async function getCampsiteAttributes(campground) {
  // eslint-disable-next-line prettier/prettier
    return (await HTTP.get(
      `campground-attributes-entries/${campground}/`,
      getConfigWithToken(),
    )).data
}

async function createStripePaymentIntent(amount, bookingId) {
  // eslint-disable-next-line prettier/prettier
    return (await HTTP.post(
    `payments/stripe/create-payment-intent/`,
      {amount, bookingId},
    getConfigWithToken()
  )).data
}

async function getStripePaymentStatus(bookingId, paymentIntent) {
  // eslint-disable-next-line prettier/prettier
    return (await HTTP.get(
    `payments/stripe/${bookingId}/get-payment-status/${paymentIntent}`,
    getConfigWithToken()
  )).data
}

const getCampgroundData = async (language = null, params = {}) => {
  if (supportFormatWebp()) {
    params.image_format = 'webp'
  }

  // If a draftbooking token is present in the URL, forward it to checkout-data
  try {
    const q = new URLSearchParams(window.location.search)
    const draft = q.get('draftbooking')
    if (draft) {
      params.draftbooking = draft
    }
  } catch (e) {
    // ignore
  }
  HTTP.defaults.baseURL = baseURL.replace('{lang}', language || lang)
  const result = await HTTP.get(
    `/checkout-data/${campground}/`,
    getConfigWithToken({ params })
  ).catch(error => onError(error))
  return result.data
}

const getData = async (url, params = {}) => {
  if (supportFormatWebp()) {
    params.image_format = 'webp'
  }

  const result = await HTTP.get(url, getConfigWithToken(params)).catch(error => onError(error))
  return result.data
}

const postData = async (url, params = {}) => {
  // Collect query params we must always forward
  const extraParams = []

  // 1) API token (if set via Api.setToken)
  if (TOKEN) {
    extraParams.push(`token=${encodeURIComponent(TOKEN)}`)
  }

  // 2) draft_booking_id (from store or current URL) – needed by cp-stripe3 backend
  try {
    const storeDraftId = App && App.$store && App.$store.getters && App.$store.getters['booking/draftBookingId']
    let draftId = storeDraftId || null
    if (!draftId) {
      const q = new URLSearchParams(window.location.search)
      const fromUrl = q.get('draft_booking_id')
      if (fromUrl) draftId = fromUrl
    }
    if (draftId && url.indexOf('draft_booking_id=') === -1) {
      extraParams.push(`draft_booking_id=${encodeURIComponent(draftId)}`)
    }
  } catch (e) {
    // ignore
  }

  if (extraParams.length) {
    url += (url.indexOf('?') !== -1 ? '&' : '?') + extraParams.join('&')
  }

  const result = await HTTP.post(url, params).catch(error => onError(error))
  return result.data
}

// on error function
const onError = error => {
  console.warn(error)
  throw error
}

const getConfigWithToken = (config = null) => {
  if (!TOKEN) {
    return config
  }

  if (!config) {
    config = {}
  }

  if (!config.params) {
    config.params = {}
  }

  config.params.token = TOKEN
  return config
}
